import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import useReferenceRange from '@hooks/useReferenceRange';

import { capitalize } from '@utils/format';

import type { SkinLesionModel } from 'generated/types';

import { determineLesionRiskLevel } from './util';

interface SkinLesionRiskProps {
  lesion: SkinLesionModel;
  index: number;
}

const messages = defineMessages({
  riskLevel: {
    id: 'skinScanCard.riskLevel',
    defaultMessage: '{level} risk',
  },
  probability: {
    id: 'coughScanDrilldown.probability.title',
    defaultMessage: '{level} Probability',
  },
});

export default function SkinLesionRisk({ lesion, index }: SkinLesionRiskProps) {
  const { formatMessage } = useIntl();
  const { parameterMap } = useReferenceRange();

  const riskLevel = React.useMemo(() => {
    return determineLesionRiskLevel(lesion.risk);
  }, [lesion.risk]);

  return (
    <Stack alignItems="center" direction="row">
      <Box
        sx={{
          backgroundColor: parameterMap[riskLevel].colour,
          borderRadius: '50%',
          width: 18,
          height: 18,
          position: 'relative',
        }}
      >
        <Typography
          color="primary.contrastText"
          variant="caption"
          sx={{
            position: 'absolute',
            transform: 'translate(-50%, -50%)',
            top: '50%',
            left: '50%',
          }}
        >
          {(index + 1).toString().padStart(2, '0')}
        </Typography>
      </Box>
      <Stack gap={1}>
        <Typography
          color="text.secondary"
          sx={{ lineHeight: 0.1 }}
          variant="body2"
        >
          {formatMessage(messages.riskLevel, {
            level: capitalize(lesion.risk),
          })}
        </Typography>
        {typeof lesion.probability === 'number' ? (
          <Typography
            color="text.secondary"
            sx={{ lineHeight: 0.1 }}
            variant="caption"
          >
            {formatMessage(messages.probability, {
              level: `${Math.round(lesion.probability * 100)}%`,
            })}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}
